<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 full-height-page-wrapper" :style="{ minHeight: getHolidaysByYear.length ? 'unset' : null }">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs :page-title="'Holidays'" />
        </div>

        <div v-if="isLoading" class="content-loading-wrapper" style="min-height: 60vh;">
          <Preloader class="centered-preloader large-spinner" />
        </div>

        <div v-else class="full-height-content">
          <div class="col-12 bg-white p-4 full-height-content">
            <div class="d-flex justify-content-between align-items-center">
              <div class="table-controls-list">
                <div class="table-controls-item filter-datepicker">
                  <b-button
                    variant="light"
                    @click.prevent="changeYear('prev')"
                  >
                    ‹
                  </b-button>
                  <Datepicker
                    :bootstrap-styling="true"
                    v-model="currentDate"
                    :format="'yyyy'"
                    minimum-view="year"
                    @selected="selectYear"
                  />
                  <b-button
                    variant="light"
                    @click.prevent="changeYear('next')"
                  >
                    ›
                  </b-button>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <Datepicker
                  class="holidays-datepicker"
                  placeholder="Select Date"
                  v-model="holidayDate"
                  :format="'dd/MM/yyyy'"
                  :disabled-dates="getDisabledDates"
                  :bootstrap-styling="true"
                />
                <b-button
                  variant="success"
                  v-if="$can('create-finances_expenses')"
                  type="button"
                  class="button ml-2"
                  :monday-first="true"
                  @click.prevent="saveDate()"
                  :disabled="isLoading || !holidayDate"
                >
                  Create
                </b-button>
              </div>
            </div>
            <div v-if="getHolidaysByYear.length" class="d-flex align-items-center mt-4">
              <template v-for="holiday in getHolidaysByYear">
                <b-badge pill class="date-badge d-flex align-items-center mr-2 mb-2" :key="`holiday-${holiday.id}`">
                  {{ _toLocalTime(holiday.date, dateFormat) }}
                  <span class="material-icons cursor-pointer ml-2" @click="removeDate(holiday.id)">close</span>
                </b-badge>
              </template>
            </div>
            <div v-else class="mt-4" style="flex: 1">
              <EmptyStateNoResults
                customIcon="event_busy"
                customIconClasses="material-symbols-outlined empty-state__icon"
                title="No holidays added yet"
                description="Add public or company holidays to keep your calendar accurate and ensure proper time-off tracking."
                buttonText="CREATE"
                @button-click="handleCreateClick"
                style="height: 100%"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import PageHeader from '@components/PageHeader'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'HolidaysPage',
  components: {
    PageHeader,
    Datepicker,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      moment,
      holidays: [],
      isLoading: false,
      dateFormat: 'DD/MM/YYYY',
      holidayDate: null,
      currentDate: null
    }
  },
  mounted () {
    this.currentDate = new Date()
    this.fetchHolidays()
  },
  computed: {
    getDisabledDates () {
      let disabledDates = this.holidays.map(holiday => new Date(holiday.date))

      return { dates: disabledDates }
    },
    getCurrentYear () {
      if (!this.currentDate) {
        return
      }
      return this.currentDate.getFullYear()
    },
    getHolidaysByYear () {
      const allHolidays = cloneDeep(this.holidays)
      const curYear = this.getCurrentYear
      return allHolidays.sort().filter(holiday => {
        return +moment(holiday.date).format('YYYY-MM-DD').split('-')[0] === curYear
      })
    }
  },
  methods: {
    async fetchHolidays () {
      this.isLoading = true
      try {
        const { data } = await this.axios({
          method: 'get',
          url: '/api/holidays',
          headers: {
            Authorization: 'Bearer' + this.$store.getters.getAccessToken
          }
        })
        this.holidays = data
      } catch (err) {
        this.errorNotifications(err)
      } finally {
        this.isLoading = false
      }
    },
    saveDate () {
      const newHoliday = moment(this.holidayDate).format('YYYY-MM-DD')
      const saveHolidayRequest = { date: newHoliday }
      this.axios.post('/api/holidays', saveHolidayRequest, {
        headers: {
          Authorization: 'Bearer' + this.$store.getters.getAccessToken
        }
      }).then(({ data }) => {
        this.holidays.push(data)
        this.holidayDate = null
        this.$forceUpdate()
      }).catch((err) => {
        this.errorNotifications(err)
      })
    },
    async removeDate (holidayId) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete?'
      )

      if (!confirm) {
        return
      }
      this.axios.delete('/api/holidays/' + holidayId, {
        headers: {
          Authorization: 'Bearer' + this.$store.getters.getAccessToken
        }
      }).then(() => {
        this.holidays = this.holidays.filter(holiday => holiday.id !== holidayId)
        this.$forceUpdate()
      }).catch((err) => {
        this.errorNotifications(err)
      })
    },
    selectYear (data) {
      this.currentDate = data
    },
    changeYear (type) {
      const currentDate = moment(this.currentDate)
      let newDate
      if (type === 'next') {
        newDate = currentDate.add(1, 'years')._d
      } else {
        newDate = currentDate
          .subtract(1, 'years')
          ._d
      }

      this.currentDate = newDate
    },
    handleCreateClick () {
      // Focus on the date picker input to prompt user to select a date
      this.$nextTick(() => {
        const datePicker = document.querySelector('.holidays-datepicker input')
        if (datePicker) {
          datePicker.focus()
          datePicker.click()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .holidays-datepicker {
    max-width: 250px;
  }
  .date-badge {
    font-size: 14px;
    padding: 5px 12px;
    color: #3D5170;
    background-color: #DEEBFF;
  }
</style>
